import React from 'react';
import { I18nextProvider } from 'react-i18next';
import DirectionProvider, { DIRECTIONS } from 'react-with-direction/dist/DirectionProvider';
import { ImgProvider, RENDER_IMAGE_WITH_PLACEHOLDER_INITIALLY } from '@wix/communities-image-lib';
import RuntimeContext from '../../../common/components/runtime-context';
import Router from '../router';
import { PermissionsContextProvider } from '../../../common/components/permissions-context';
import { initI18n } from '../../services/i18n-provider';
import { getCategory } from '../../../common/selectors/categories-selectors';
import {
  getLanguage,
  isDebug,
  isProduction,
} from '../../../common/store/basic-params/basic-params-selectors';
import { isWebpSupported } from '../../../common/services/is-webp-supported';
import { getTranslations } from '../../../common/store/translations/translations-selectors';
import { FastFormProvider } from '../../../common/components/fast-form';
import { usePromisifiedAction } from '../../../common/actions-promisifier/use-promisified-action';
import { getFastFormSubmitAction } from '../../../common/components/fast-form/store/fast-form-selectors';
import { withPromisifiedOpenModal } from '../../../common/modals/framework/store/modal-actions';
import '@wix/communities-forum-rce/dist/src/index.global.scss';

const permissionsResourceResolver = state => resource => {
  if (resource.categoryId) {
    resource = getCategory(state, resource.categoryId);
  }
  return resource;
};

const pinterestPinId = 'pinterest-pin-script';
const loadPinterestPin = language => {
  const s = document.createElement('script');
  s.setAttribute('src', '//assets.pinterest.com/js/pinit.js');
  s.setAttribute('data-pin-hover', 'true');
  s.setAttribute('data-pin-lang', language);
  s.setAttribute('data-pin-save', 'true');
  s.async = false;
  s.id = pinterestPinId;
  document.querySelector('body').appendChild(s);
};

class AppRoot extends React.Component {
  constructor(props) {
    super(props);
    if (
      typeof window !== 'undefined' &&
      !window.PinUtils &&
      !document.getElementById(pinterestPinId)
    ) {
      loadPinterestPin(getLanguage(props.state));
    }
  }

  getI18nConfig = (language, translations) => {
    if (this.lastLanguage !== language) {
      this.i18nConfig = initI18n(language, translations);
      this.lastLanguage = language;
    }
    return this.i18nConfig;
  };

  render() {
    const { state, actions, actionsPromisified, host, isRTL } = this.props;
    const i18n = this.getI18nConfig(getLanguage(state), getTranslations(state));

    if (isDebug(state) || !isProduction(state)) {
      console.log('AppRoot', this.props);
    }

    const connectedActionsPromisified = Object.keys(actionsPromisified).reduce(
      (wrapped, actionName) => {
        wrapped[actionName] = usePromisifiedAction(actionsPromisified[actionName], state);
        return wrapped;
      },
      {},
    );

    return (
      <RuntimeContext.Provider
        value={{
          state,
          actions: {
            ...actions,
            ...connectedActionsPromisified,
            openModal: withPromisifiedOpenModal(actions.openModal),
          },
          host,
        }}
      >
        <PermissionsContextProvider state={state} resourceResolver={permissionsResourceResolver}>
          <I18nextProvider i18n={i18n}>
            <DirectionProvider direction={isRTL ? DIRECTIONS.RTL : DIRECTIONS.LTR}>
              <ImgProvider
                renderMode={RENDER_IMAGE_WITH_PLACEHOLDER_INITIALLY}
                format={isWebpSupported() ? 'webp' : 'png'}
              >
                <FastFormProvider
                  submitAction={getFastFormSubmitAction(state)}
                  onSubmit={actions.fastFormSubmitCallback}
                >
                  <Router />
                </FastFormProvider>
              </ImgProvider>
            </DirectionProvider>
          </I18nextProvider>
        </PermissionsContextProvider>
      </RuntimeContext.Provider>
    );
  }
}

export default AppRoot;
